import { FC, useMemo, useState } from "react";
import { Popover, Text, Tooltip } from "@mantine/core";

import { IssueStatus } from "@protos/common/v1/common";
import {
  AggregateIssueState,
  Issue as IssueProto,
  IssueType,
} from "@protos/issues/v1/issues";
import { usePermissions, useRepos } from "@shared/hooks";
import { Permission } from "@shared/types";
import { getRuleBoardSlugFromPolicySlug } from "@shared/utils";

import { useSaveIgnoreOptions } from "../../hooks";
import { useSelectAllFindings } from "../../stores";
import { TriageIgnorePathDisableRuleInfo } from "../../types";
import { ImprovedTriageOptions } from "../ImprovedTriageOptions";

import { ImprovedInlineTriagePopoverTarget } from "./ImprovedInlineTriagePopoverTarget";
import { MinimalInlineTriagePopoverTarget } from "./MinimalInlineTriagePopoverTarget";

export interface InlineTriageButtonProps {
  issue: IssueProto;
  issueType: IssueType;
  onIgnorePathOrRule?: (info: TriageIgnorePathDisableRuleInfo) => void;
  refetchAfterTriage?: boolean;
  minimal?: boolean;
}

/**
 *
 * Button that can be used inline to triage an issue. It will open a popover with
 * triage options (options leave a note and a reason for ignoring, if applicable).
 * @state The current triage state of the issue. If state is "reopened", the action
 *        will be to ignore. If the state is "ignored", the action will be to reopen.
 * @issue The issue to triage.
 * @onIgnorePathOrRule runs when user ignores a path or removes a rule to pass information to the parent.
 *                     The parent also handles when to reload the issues query.
 * @param refetchAfterTriage if false, it is up to the parent component to invalidate the issues query
 *                           after triaging. Defaults to true.
 * @param minimal if true, will render a minimally styled button
 */
export const InlineTriageButton: FC<InlineTriageButtonProps> = ({
  issue,
  issueType,
  onIgnorePathOrRule,
  refetchAfterTriage = true,
  minimal,
}) => {
  const { data: repos, isLoading: reposLoading } = useRepos();
  const [permissions] = usePermissions();
  const findingRepo = repos?.find((r) => r.name === issue.repository?.name);
  const isFixed = issue.status === IssueStatus.FIXED;
  const isRemoved = issue.status === IssueStatus.REMOVED;

  const [open, setOpen] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState(false);
  // for IgnoreFutureFindingsOptions
  const [ignoreOption, setIgnoreOption] = useState<string>();
  const [disableRule, setDisableRule] = useState(false);
  const selectAllFindings = useSelectAllFindings();

  const ruleBoardSlug = useMemo(
    () => getRuleBoardSlugFromPolicySlug(issue.policySlug),
    [issue.policySlug]
  );

  const saveIgnoreOptions = useSaveIgnoreOptions(
    issue.filePath ?? "",
    issue.rulePath,
    issue.issueType,
    findingRepo,
    ruleBoardSlug
  );

  const hasPermissionToChangePolicy =
    permissions.includes(Permission.panel_policy) &&
    permissions.includes(Permission.panel_projects);

  const showIgnoreOptions =
    hasPermissionToChangePolicy &&
    [
      AggregateIssueState.OPEN,
      AggregateIssueState.REVIEWING,
      AggregateIssueState.FIXING,
    ].includes(issue.aggregateState);

  const onSaveClick = async () => {
    setIsSaving(true);
    // save ignore options, if applicable
    if (showIgnoreOptions && (ignoreOption || disableRule)) {
      const ignoreOptionResults = await saveIgnoreOptions(
        disableRule,
        ignoreOption
      );
      onIgnorePathOrRule && onIgnorePathOrRule(ignoreOptionResults);
    }
    setIsSaving(false);
    setOpen(false);
  };

  const currentTriageState = (() => {
    switch (issue.aggregateState) {
      case AggregateIssueState.OPEN:
        return "reopened";
      case AggregateIssueState.REVIEWING:
        return "reviewing";
      case AggregateIssueState.FIXING:
        return "fixing";
      default:
        return "ignored";
    }
  })();

  return (
    <Popover
      shadow="sm"
      width={300}
      trapFocus
      withinPortal
      opened={open}
      onChange={(opened) => setOpen(opened)}
      position="bottom-end"
    >
      <Popover.Target>
        <Tooltip
          label={
            <>
              <Text>Detected to be fixed by a scan. </Text>
              <Text>Fixed statuses can't be modified.</Text>
            </>
          }
          disabled={!isFixed}
          multiline
          w={210}
          fz="xs"
          position="bottom"
          withinPortal
        >
          <div>
            {minimal ? (
              <MinimalInlineTriagePopoverTarget
                issueState={issue.aggregateState}
                isFixed={isFixed}
                onClick={() => setOpen((open) => !open)}
                isLoading={isSaving}
              />
            ) : (
              <ImprovedInlineTriagePopoverTarget
                issueState={issue.aggregateState}
                isFixed={isFixed}
                isRemoved={isRemoved}
                onClick={() => setOpen((open) => !open)}
                isLoading={isSaving}
              />
            )}
          </div>
        </Tooltip>
      </Popover.Target>
      <Popover.Dropdown p={0}>
        <ImprovedTriageOptions
          issueType={issueType}
          currentTriageState={currentTriageState}
          findingIds={[issue.id]}
          // The next 3 lines are for triage V2
          triageFilter={null}
          issueParentIds={[issue.issueParentId]}
          ruleIds={[]}
          disableTicketing={issue.externalTicket !== undefined}
          onSaveClick={onSaveClick}
          ignoreOptions={
            showIgnoreOptions
              ? {
                  ignoreOption,
                  setIgnoreOption,
                  disableRule,
                  setDisableRule,
                  findingPath: issue.filePath ?? "",
                  showIgnoreFilesOption: !!findingRepo,
                  showDisableRuleOption: ruleBoardSlug !== "unknown",
                }
              : undefined
          }
          isLoadingIgnoreOptions={showIgnoreOptions && reposLoading}
          refetchAfterTriage={refetchAfterTriage}
          selectAllFindings={selectAllFindings}
        />
      </Popover.Dropdown>
    </Popover>
  );
};
